import "./ArticleWithAssets.scss"
import pictogramArticle from "assets/img/pictogram/article.svg"
import IconInspection from "assets/img/icon-inspection.svg?react"
import IconInspectionCheck from "assets/img/icon-inspection-check.svg?react"
import IconInspectionWarning from "assets/img/icon-inspection-warning.svg?react"

import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import cx from "classnames"

import { Grid, GridItem } from "components/Grid"
import { Flex } from "components/Flex"
import Pictogram from "components/Pictogram/Pictogram"
import Spinner from "components/Spinner/Spinner"
import StatusIndicator, { StatusIndicatorState } from "components/StatusIndicator/StatusIndicator"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import Button from "components/Button/Button"
import ReservationArticle from "models/ReservationArticle"
import ReservationCartArticle from "models/ReservationCartArticle"
import ReservationCartAsset from "models/ReservationCartAsset"
import ReservationAsset from "models/ReservationAsset"
import Lmid from "components/Lmid/Lmid"
import ReservationAssetShippingStatuses, { IStatusIcon } from "components/ReservationAssetShippingStatuses/ReservationAssetShippingStatuses"
import { Purpose } from "config/constants"
import { Guid } from "@/types"
import Text from "components/Text/Text"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"

interface IArticleWithAssetsProps {
    article: ReservationArticle | ReservationCartArticle
    assets?: ReservationAsset[] | ReservationCartAsset[]
    isNodeArticle?: boolean
    assetCount?: number
    statusIndicator?: StatusIndicatorState
    purpose?: Purpose
    showLmidForAutomaticAssets?: boolean
    clickableLmid?: boolean
    inspectedAssetIds?: string[]
    damagedAssetIds?: string[]
}

const ArticleWithAssets: FC<IArticleWithAssetsProps> = ({
    article,
    assets,
    isNodeArticle,
    assetCount,
    purpose,
    statusIndicator,
    showLmidForAutomaticAssets,
    clickableLmid,
    inspectedAssetIds,
    damagedAssetIds,
}) => {
    const { t } = useTranslation()
    const isExpandable = !!assets && assets.length > 0
    const [isExpanded, setIsExpanded] = useState(false)
    const assetsLabel = t('common.xAssets', { count: typeof assetCount === "number" ? assetCount : article.quantity })

    const hasInspectedAssets = useMemo(() => {
        if (!inspectedAssetIds || inspectedAssetIds.length === 0) return false
        return article.assets.some(asset => asset.assetId && inspectedAssetIds.includes(asset.assetId))
    }, [article.assets, inspectedAssetIds])

    function toggleAssets() {
        if (!isExpandable) return
        setIsExpanded(currentIsExpanded => !currentIsExpanded)
    }

    if (!article.isHydrated) {
        return (
            <div className="article-with-assets__placeholder"
                data-testid="article-with-assets">
                <Spinner size={32} />
            </div>
        )
    }

    function getAssetInspectionIcons(assetId?: Guid): IStatusIcon[] {
        if (!assetId) return []
        if (damagedAssetIds?.includes(assetId)) {
            return [
                { img: IconInspectionWarning }
            ]
        }
        if (inspectedAssetIds?.includes(assetId)) {
            return [
                { img: IconInspectionCheck }
            ]
        }
        return []
    }

    return (
        <Grid
            columns="6.4rem 1fr max-content"
            gap="1.6rem 2.4rem"
            data-testid="article-with-assets">
            <GridItem>
                <Pictogram
                    src={article.articleThumbnailUri || pictogramArticle}
                    fullSizeSrc={article.articleImageUri}
                    cover={!!article.articleThumbnailUri} />
            </GridItem>
            <GridItem alignSelf="center">
                <ArticleDescription
                    articleDescription={article.articleDescription}
                    articleId={article.articleId}
                    isNodeArticle={isNodeArticle}
                    withNodeArticleTooltip
                />
                <FlexibleContent
                    text={`${t('common.artNo')}: ${article.articleNo}`}
                    className="mt-1"
                />
            </GridItem>
            <Flex flow="column" justifyContent="center" alignItems="flex-end" gap="0.8rem">
                <Flex alignItems="center" justifyContent="flex-end" gap="1.6rem">
                    {hasInspectedAssets &&
                        <IconInspection />
                    }
                    {statusIndicator &&
                        <StatusIndicator
                            state={statusIndicator}
                        />
                    }
                </Flex>
                <Grid columns="minmax(0, auto) max-content">
                    <GridItem>
                        {isExpandable ?
                            <Button
                                theme="link"
                                className={cx([
                                    "article-with-assets__assets-toggle",
                                    { "article-with-assets__assets-toggle--expanded": isExpanded }
                                ])}
                                label={assetsLabel}
                                onClick={toggleAssets}
                            />
                            :
                            assetsLabel
                        }
                    </GridItem>
                </Grid>
            </Flex>
            {isExpandable && isExpanded &&
                <GridItem area="2 / 2 / 2 / -1">
                    {assets?.map((asset: ReservationAsset | ReservationCartAsset, idx: number) => (
                        <div className="border-grey-100-between py-2" key={idx}>
                            <Grid columns="minmax(0, auto) auto" gap="1.6rem">
                                <GridItem>
                                    <Grid columns="max-content minmax(0, auto)" gap="1.6rem">
                                        <GridItem>{t('common.lmid')}:</GridItem>
                                        <GridItem>
                                            {asset.isHydrated ?
                                                <>
                                                    {!!asset.assetId &&
                                                        <>
                                                            {(asset.hasUserAssignment || asset.unplanned || asset.isReleasedOrStored || asset.isPushedToStock || showLmidForAutomaticAssets)
                                                                ? <Lmid
                                                                    lmid={asset.isHydrated ? `${asset.lmid}${asset.unplanned ? ` (${t('reservation.notPlanned')})` : ''}` : t('common.loading')}
                                                                    labeled={asset.isLabeled}
                                                                    assetId={clickableLmid ? asset.assetId : undefined}
                                                                    inCollection={asset.linkedToAssetCollection} />
                                                                : t('common.automaticSelection')
                                                            }
                                                        </>
                                                    }
                                                    {!asset.assetId &&
                                                        t('common.notAvailable')
                                                    }
                                                </>
                                                :
                                                <>{t('common.loading')}</>
                                            }
                                        </GridItem>
                                    </Grid>
                                    {!!asset.altArticleDescription &&
                                        <Text type="pSmallest" className="color-grey-500 mt-1">
                                            <ArticleDescription articleDescription={asset.altArticleDescription} />
                                        </Text>
                                    }
                                </GridItem>
                                <GridItem justifySelf="end">
                                    {asset.isHydrated && !!asset.assetId &&
                                        <ReservationAssetShippingStatuses
                                            purpose={purpose}
                                            asset={asset}
                                            wrap
                                            prependIcons={getAssetInspectionIcons(asset.assetId)}
                                        />
                                    }
                                </GridItem>
                            </Grid>
                        </div>
                    ))}
                </GridItem>
            }
        </Grid>
    )
}

export default ArticleWithAssets
